import { ClientAdministrationRow, ClientFigures } from "@/data/models/ClientAdministrationRow";
import { ExternalSystemRow, PartnerStatus } from "@/data/models/ClientPartner";
import { DeviceReportFilters, emptyDeviceReportFilters, ReportAssignment } from "@/data/models/DeviceReportFilters";
import { InfoConfigVersionList } from "@/data/models/InfoConfigAdministration";
import {
  ClientListFilter,
  DEFAULT_CLIENT_LIST_SELECTION,
  matchesClientListFilter,
  matchesClientListSelection,
} from "@/utils/ClientAdministrationList";
import { clientFigure } from "@/utils/ClientListFigures";
import { filterExternalSystems } from "@/utils/ClientPartners";

/** The figures on the admin cockpit's Mandanten card, in display order. */
export const CLIENT_STATUS_FIGURE_FILTERS: ClientListFilter[] = [
  ClientListFilter.ALL,
  ClientListFilter.CLIENT,
  ClientListFilter.IRES,
  ClientListFilter.MASTER,
  ClientListFilter.LIVE,
  ClientListFilter.NOT_LIVE,
  ClientListFilter.BLOCKED,
  ClientListFilter.ACTIVE,
  ClientListFilter.INACTIVE,
  ClientListFilter.DELETED,
];

/** The figures on the admin cockpit's contracts and app card, in display order. */
export const CLIENT_CONTRACT_FIGURE_FILTERS: ClientListFilter[] = [
  ClientListFilter.CONTRACT_PURCHASE,
  ClientListFilter.CONTRACT_PERMANENT_RENT,
  ClientListFilter.CONTRACT_TESTING,
  ClientListFilter.SERVICE_CONTRACT,
  ClientListFilter.HAS_APP,
];

/** One figure: how many clients the Mandanten list shows under `filter`. */
export interface ClientFigureCount {
  filter: ClientListFilter;
  count: number;
}

/**
 * Counts the clients under each filter, in the order given.
 *
 * Uses the Mandanten list's own `matchesClientListFilter` rather than a definition of its own, so a
 * figure always equals the number of rows the list shows when the figure is clicked (with no search).
 */
export function buildClientFigureCounts(
  clients: ClientAdministrationRow[],
  filters: ClientListFilter[]
): ClientFigureCount[] {
  return filters.map((filter) => ({
    filter,
    count: clients.filter((client) => matchesClientListFilter(client, filter)).length,
  }));
}

/**
 * The route query that opens the Mandanten list under `filter`: none for ALL, as the list writes it
 * itself, so going back and forth leaves identical URLs.
 */
export function clientListQueryFor(filter: ClientListFilter): Record<string, string> {
  return filter === ClientListFilter.ALL ? {} : { filter };
}

/**
 * The clients the Gesamt card adds up, as the Mandanten list's selection: Kunden in Echtbetrieb, not
 * deleted (Taimoor, 2026-10-28). IRES's own accounts and test, demo or Lager accounts would only blur
 * the business figures.
 */
export const LIVE_CUSTOMER_SELECTION: ClientListFilter[] = [
  ClientListFilter.CLIENT,
  ClientListFilter.LIVE,
  ...DEFAULT_CLIENT_LIST_SELECTION,
];

/** The figures on the Gesamt card, in display order, each with its label. */
export const CLIENT_TOTAL_FIGURES: ReadonlyArray<{ field: keyof ClientFigures; labelKey: string }> = [
  { field: "numProjects", labelKey: "admin.cockpit.totals.projects" },
  { field: "numOldProjects", labelKey: "admin.cockpit.totals.oldProjects" },
  { field: "numReports", labelKey: "admin.clients.columns.reports" },
  { field: "numCurrentPanels", labelKey: "admin.clients.columns.panels" },
  { field: "numCurrentComBoxes", labelKey: "admin.clients.columns.comBoxes" },
  { field: "numCurrentMinis", labelKey: "admin.clients.columns.minis" },
  { field: "numUsers", labelKey: "admin.clients.columns.users" },
];

/**
 * One figure added up over the live customers, from IRES Cloud's hourly recount.
 *
 * A client not counted yet (created since the last run) adds nothing rather than blanking the total
 * until the next run, which may be the next morning; such a client has no projects or devices yet.
 * Null only when no live customer carries the figure at all (an IRES Cloud that does not serve it), so
 * a missing figure never reads as 0.
 */
export function liveCustomerTotal(clients: ClientAdministrationRow[], field: keyof ClientFigures): number | null {
  const values = clients
    .filter((client) => matchesClientListSelection(client, LIVE_CUSTOMER_SELECTION))
    .map((client) => clientFigure(client.figures, field))
    .filter((value): value is number => value !== null);

  return values.length === 0 ? null : values.reduce((sum, value) => sum + value, 0);
}

/**
 * The route query that opens the Mandanten list under a whole selection: the filter repeated, which
 * the list joins (see `clientListSelectionForLink`).
 */
export function clientListQueryForSelection(selection: ClientListFilter[]): Record<string, string[]> {
  return { filter: [...selection] };
}

/** The Externe Systeme states the Aufgaben card counts: the ones that are not running on their own. */
export const EXTERNAL_SYSTEM_TASK_STATUSES: PartnerStatus[] = ["SETUP", "TEST", "PAUSED"];

/** How many systems the Externe Systeme list shows under `status`, through its own filter. */
export function externalSystemCount(rows: ExternalSystemRow[], status: PartnerStatus): number {
  return filterExternalSystems(rows, status, "").length;
}

/** The Beschreibungen card's figures: the room version the fleet follows and who sets their own. */
export interface InfoConfigTargetFigures {
  globalTarget: string | null;
  /** Clients with a target of their own, the "Ziel von n Mandanten" badges added up. */
  clientTargets: number;
  /** Clients whose test users have a target, the "Testziel bei n Mandanten" badges added up. */
  testTargets: number;
}

/**
 * The room Beschreibung targets, from the version list's own badges, so each figure is what the list's
 * Ziele filter shows. The legacy leak-detection files are left out: nothing new targets them.
 */
export function infoConfigTargetFigures(list: InfoConfigVersionList): InfoConfigTargetFigures {
  return {
    globalTarget: list.globalRoomTarget,
    clientTargets: list.room.reduce((sum, row) => sum + row.clientTargetCount, 0),
    testTargets: list.room.reduce((sum, row) => sum + row.testTargetCount, 0),
  };
}

/** The Testberichte filters for the reports still waiting for their device ("Nicht zugeordnet"). */
export function waitingDeviceReportFilters(): DeviceReportFilters {
  return { ...emptyDeviceReportFilters(), assignment: ReportAssignment.UNASSIGNED };
}
